/**
 * Utility function to scroll to an element by dataId
 * Uses polling to wait for the element to be available in the DOM
 */

const SCROLL_MAX_RETRIES = 20;
const SCROLL_RETRY_INTERVAL = 100; // milliseconds

export type ScrollToElementOptions = {
  behavior?: ScrollBehavior;
};

/**
 * Scrolls an element into view
 */
const scrollElementIntoView = (element: Element, behavior: ScrollBehavior = "auto"): void => {
  element.scrollIntoView({
    behavior,
    block: "start",
    inline: "nearest",
  });
};

/**
 * Scrolls to an element by dataId using polling
 * This is useful when the element might not be immediately available in the DOM
 * @param dataId - The data-id attribute value or element id to scroll to
 * @param maxRetries - Maximum number of retry attempts (default: 20)
 * @param retryInterval - Interval between retries in milliseconds (default: 100)
 */
export const scrollToElementByDataId = (
  dataId: string,
  maxRetries: number = SCROLL_MAX_RETRIES,
  retryInterval: number = SCROLL_RETRY_INTERVAL,
  options: ScrollToElementOptions = {},
): void => {
  if (!dataId) {
    console.warn("[scrollToElementByDataId] DataId is required.");
    return;
  }

  const { behavior = "auto" } = options;
  let retries = 0;

  const pollForElement = (): void => {
    retries++;

    // Try to find element by data-id attribute first, then by id
    const element =
      document.querySelector(`[data-id="${dataId}"]`) || document.getElementById(dataId);

    if (element) {
      scrollElementIntoView(element, behavior);
      return;
    }

    if (retries < maxRetries) {
      setTimeout(pollForElement, retryInterval);
    } else {
      console.warn(
        `[scrollToElementByDataId] Element with dataId "${dataId}" not found after ${maxRetries} attempts.`,
      );
    }
  };

  pollForElement();
};

export const resetScrollContainer = (el?: HTMLElement | null): void => {
  el?.scrollTo({ top: 0, left: 0 });
};
